import type { ReactNode } from 'react';

/**
 * §13 — Küçük durum rozeti (insight durumu, hedef durumu, segment etiketi, kanal…).
 * Referans DXP admin panelinin rozet dili: köşe yarıçapı `rounded-md`, 11px metin,
 * açık zemin + koyu metin (kontrast ≥ 7:1), çerçevesiz.
 */

export type BadgeTone =
  | 'neutral'
  | 'petrol'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'outline';

export interface BadgeProps {
  tone?: BadgeTone;
  /** Metinden önce gösterilen küçük nokta — durum listelerinde tarama kolaylığı. */
  dot?: boolean;
  size?: 'sm' | 'md';
  title?: string;
  className?: string;
  children: ReactNode;
}

/** Ton → zemin + metin sınıfları (referans panelin nötr/marka paleti). */
const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-[#f3f4f6] text-[#374151]',
  petrol: 'bg-[#eef2ff] text-[#4338ca]',
  accent: 'bg-[#fff4ec] text-brand',
  success: 'bg-[#ecfdf5] text-emerald-700',
  warning: 'bg-[#fffbeb] text-amber-700',
  danger: 'bg-[#fef2f2] text-red-700',
  outline: 'border border-[#e5e7eb] bg-white text-[#6b7280]',
};

/** Nokta rengi tonun metin rengiyle aynı aileden, bir tık doygun. */
const DOTS: Record<BadgeTone, string> = {
  neutral: 'bg-[#9ca3af]',
  petrol: 'bg-[#6366f1]',
  accent: 'bg-brand',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  outline: 'bg-[#9ca3af]',
};

export default function Badge({
  tone = 'neutral',
  dot = false,
  size = 'md',
  title,
  className = '',
  children,
}: BadgeProps) {
  const sizing = size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-[11px]';
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md font-medium ${TONES[tone]} ${sizing} ${className}`}
    >
      {dot ? (
        <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${DOTS[tone]}`} />
      ) : null}
      {children}
    </span>
  );
}
